import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import MotherDayPage from './MotherDayPage';
import { OrderModalProvider } from './components/OrderModalProvider';
import './App.css';

// ----- Types -----
interface Product {
  name: string;
  description: string;
  price: string;
  icon: string;
  badge?: string;
}

interface Service {
  icon: string;
  title: string;
  description: string;
}

// ----- Data -----
const products: Product[] = [
  {
    name: 'Jardin de Rêve',
    description: 'An enchanting blend of garden roses, peonies, and lavender in soft blush and ivory tones.',
    price: '€185',
    icon: 'fas fa-seedling',
    badge: 'Best Seller',
  },
  {
    name: 'L\'Élégance Parisienne',
    description: 'A sophisticated arrangement of white orchids, anemones, and eucalyptus in a ceramic vase.',
    price: '€220',
    icon: 'fas fa-spa',
    badge: 'New',
  },
  {
    name: 'Bouquet Sauvage',
    description: 'Wildflowers, dried grasses, and seasonal blooms tied with silk ribbon — effortlessly romantic.',
    price: '€145',
    icon: 'fas fa-leaf',
  },
  {
    name: 'Cœur de Rose',
    description: 'A dozen long-stemmed French roses in deep crimson, wrapped in velvet paper.',
    price: '€175',
    icon: 'fas fa-rose',
    badge: 'Classic',
  },
  {
    name: 'Jardin Blanc',
    description: 'An all-white monochrome arrangement of lilies, hydrangeas, and baby\'s breath.',
    price: '€195',
    icon: 'fas fa-dove',
  },
  {
    name: 'Provence en Fleurs',
    description: 'A sun-kissed medley of sunflowers, lavender, and olive branches from the South of France.',
    price: '€160',
    icon: 'fas fa-sun',
    badge: 'Seasonal',
  },
];

const services: Service[] = [
  {
    icon: 'fas fa-bouquet',
    title: 'Artisan Bouquets',
    description: 'Hand-tied bouquets crafted daily with the freshest seasonal blooms from our own nursery and trusted local growers.',
  },
  {
    icon: 'fas fa-glass-cheers',
    title: 'Event Florals',
    description: 'From intimate weddings to grand celebrations, we design bespoke floral installations that tell your unique story.',
  },
  {
    icon: 'fas fa-truck',
    title: 'Concierge Delivery',
    description: 'White-glove delivery service across Paris and the Île-de-France region, ensuring your flowers arrive in perfect condition.',
  },
  {
    icon: 'fas fa-hand-sparkles',
    title: 'Botanical Workshops',
    description: 'Join us for intimate floral design workshops in our atelier, where you\'ll learn the art of French flower arranging.',
  },
  {
    icon: 'fas fa-calendar-check',
    title: 'Subscription Service',
    description: 'A weekly or monthly delivery of fresh seasonal arrangements to keep your home or office in constant bloom.',
  },
  {
    icon: 'fas fa-tree',
    title: 'Garden Design',
    description: 'Full-service garden consultation and design, bringing the Mon Plaisir aesthetic to your outdoor spaces.',
  },
];

// ----- ScrollReveal Component -----
const ScrollReveal: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = '',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`animate-on-scroll ${visible ? 'visible' : ''} ${className}`}>
      {children}
    </div>
  );
};

// ----- Navbar -----
const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <a href="#hero" className="logo" onClick={(e) => handleNavClick(e, 'hero')}>
        <img src="/img/monplaisir-logo.png" alt="" aria-hidden="true" />
        <span className="logo-text">Mon Plaisir <span>Nursery</span></span>
      </a>
      <div
        className={`mobile-toggle ${mobileOpen ? 'active' : ''}`}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>
      <ul className={`nav-links ${mobileOpen ? 'open' : ''}`}>
        <li><a href="#featured" onClick={(e) => handleNavClick(e, 'featured')}>Arrangements</a></li>
        <li><a href="#about" onClick={(e) => handleNavClick(e, 'about')}>Our Story</a></li>
        <li><a href="#services" onClick={(e) => handleNavClick(e, 'services')}>Services</a></li>
        <li><a href="#contact" onClick={(e) => handleNavClick(e, 'contact')}>Contact</a></li>
        <li><Link to="/mothers-day" className="nav-mothers-day">Mother's Day</Link></li>
      </ul>
    </nav>
  );
};

// ----- Hero -----
const Hero: React.FC = () => {
  const handleCTA = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('featured');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="hero">
      <div className="hero-bg" />
      <div className="hero-pattern" />
      <div className="hero-overlay" />
      <div className="hero-content">
        <div className="hero-badge">Depuis 1987 · Paris</div>
        <h1 className="hero-title">
          Mon Plaisir <span className="italic">Nursery</span>
        </h1>
        <p className="hero-subtitle">
          Where Parisian elegance meets the poetry of flowers —<br />
          artisanal blooms cultivated with passion since three generations.
        </p>
        <a href="#featured" className="hero-cta" onClick={handleCTA}>
          Explore Our Collection <i className="fas fa-arrow-right" />
        </a>
      </div>
      <div className="hero-scroll">
        <span>Scroll</span>
        <i className="fas fa-chevron-down" />
      </div>
    </section>
  );
};

// ----- Featured Products -----
const Featured: React.FC = () => {
  return (
    <section id="featured" className="section featured">
      <div className="section-header">
        <p className="section-label">Our Collection</p>
        <h2 className="section-title">
          Featured <span className="italic">Arrangements</span>
        </h2>
        <div className="section-divider" />
        <p className="section-subtitle">
          Each arrangement is a work of art, thoughtfully composed with the finest seasonal blooms
        </p>
      </div>
      <div className="products-grid">
        {products.map((product, index) => (
          <div key={index} className="product-card">
            <div className="product-image">
              <i className={`${product.icon} flower-icon`} />
              <div className="overlay" />
              {product.badge && <span className="product-badge">{product.badge}</span>}
              <div className="quick-view">View Details</div>
            </div>
            <div className="product-info">
              <h3 className="product-name">{product.name}</h3>
              <p className="product-description">{product.description}</p>
              <span className="product-price">{product.price}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ----- About -----
const About: React.FC = () => {
  return (
    <section id="about" className="section about">
      <div className="section-header">
        <p className="section-label">Our Story</p>
        <h2 className="section-title">
          A Legacy of <span className="italic">Beauty</span>
        </h2>
        <div className="section-divider" />
      </div>
      <div className="about-content">
        <ScrollReveal>
          <div className="about-image">
            <div className="icon-wrapper">
              <i className="fas fa-leaf" />
            </div>
          </div>
        </ScrollReveal>
        <ScrollReveal>
          <div className="about-text">
            <h3>
              Cultivating <span className="italic">Elegance</span> Since 1987
            </h3>
            <p>
              Nestled in the heart of the 7th arrondissement, Mon Plaisir Nursery was born from a
              simple yet profound belief: that flowers have the power to transform moments into
              memories. What began as a small family greenhouse has blossomed into one of Paris's
              most cherished floral ateliers.
            </p>
            <p>
              Our philosophy is rooted in the French tradition of <em>"l'art de vivre"</em> — the
              art of living beautifully. We source our blooms from the finest growers across France
              and Europe, selecting each stem by hand for its character, fragrance, and vitality.
            </p>
            <p>
              Every arrangement we create is a dialogue between nature and artistry, a celebration
              of the seasons, and a testament to our unwavering commitment to quality and beauty.
            </p>
            <div className="signature">— Marie Dubois, Founder & Creative Director</div>
          </div>
        </ScrollReveal>
      </div>
      <ScrollReveal>
        <div className="about-values">
          <div className="value-item">
            <i className="fas fa-seedling" />
            <h4>Sustainable Sourcing</h4>
          </div>
          <div className="value-item">
            <i className="fas fa-hand-holding-heart" />
            <h4>Artisanal Craftsmanship</h4>
          </div>
          <div className="value-item">
            <i className="fas fa-star" />
            <h4>Uncompromising Quality</h4>
          </div>
        </div>
      </ScrollReveal>
    </section>
  );
};

// ----- Services -----
const Services: React.FC = () => {
  return (
    <section id="services" className="section services">
      <div className="section-header">
        <p className="section-label">What We Offer</p>
        <h2 className="section-title">
          Our <span className="italic">Services</span>
        </h2>
        <div className="section-divider" />
        <p className="section-subtitle">
          From everyday elegance to extraordinary celebrations, we bring your floral visions to life
        </p>
      </div>
      <div className="services-grid">
        {services.map((service, index) => (
          <div key={index} className="service-card">
            <div className="service-icon">
              <i className={service.icon} />
            </div>
            <h3>{service.title}</h3>
            <p>{service.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// ----- Footer / Contact -----
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer id="contact" className="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <img src="/img/monplaisir-logo.png" alt="Mon Plaisir Nursery" />
          <h2>Mon Plaisir <span>Nursery</span></h2>
          <p>
            A Parisian floral atelier dedicated to the art of botanical beauty — where every petal
            tells a story.
          </p>
          <div className="footer-social">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="fab fa-instagram" /></a>
            <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" aria-label="Pinterest"><i className="fab fa-pinterest-p" /></a>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="fab fa-facebook-f" /></a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" aria-label="TikTok"><i className="fab fa-tiktok" /></a>
          </div>
        </div>
        <div>
          <h4>Visit Us</h4>
          <ul>
            <li><i className="fas fa-map-marker-alt" /> 24 Rue de Grenelle</li>
            <li>75007 Paris, France</li>
            <li style={{ marginTop: '1rem' }}>
              <i className="fas fa-clock" /> Mon–Sat: 9:00–19:00
            </li>
            <li><i className="fas fa-clock" /> Sun: 10:00–17:00</li>
          </ul>
        </div>
        <div>
          <h4>Contact</h4>
          <ul>
            <li><i className="fas fa-phone" /> +33 1 42 84 12 34</li>
            <li><i className="fas fa-envelope" /> hello@monplaisir.fr</li>
            <li style={{ marginTop: '1rem' }}>
              <a href="/consultation"><i className="fas fa-calendar-alt" /> Book a Consultation</a>
            </li>
          </ul>
        </div>
        <div>
          <h4>Quick Links</h4>
          <ul>
            <li><a href="#featured">Our Collection</a></li>
            <li><a href="#about">Our Story</a></li>
            <li><a href="#services">Services</a></li>
            <li><Link to="/mothers-day">Mother's Day Collection</Link></li>
            <li><a href="/delivery">Delivery Information</a></li>
            <li><a href="/care-guide">Care Guide</a></li>
            <li><a href="/privacy">Privacy Policy</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <span>&copy; {currentYear} Mon Plaisir Nursery. All rights reserved.</span>
        <span>
          Crafted with <i className="fas fa-heart" style={{ color: 'var(--rose)', margin: '0 4px' }} /> in Paris
        </span>
      </div>
    </footer>
  );
};

// ----- Home Page -----
const HomePage: React.FC = () => {
  return (
    <>
      <Navbar />
      <Hero />
      <Featured />
      <About />
      <Services />
      <Footer />
    </>
  );
};

// ----- App -----
const App: React.FC = () => {
  return (
    <OrderModalProvider>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/" element={<MotherDayPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/mothers-day" element={<MotherDayPage />} />
          </Routes>
        </div>
      </Router>
    </OrderModalProvider>
  );
};

export default App;
